import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DATA_ACCESS_ALL_STATUS,
  DATA_ACCESS_DISCLAIMER_PLACEHOLDER,
  QueryKeys,
} from "@/constants";
import useAuth from "@/context/auth-provider";
import { useDataAccessUpdateMutation } from "@/hooks/use-data-access";
import { queryClient } from "@/hooks/use-query-client";
import { sanitizeHtmlWithPlaceholders } from "@/lib/data-access-utils";
import type { DataAccessConfigResponseType } from "@/model/data-access";
import type { Demographics, Relation } from "@/model/demographics";
import {
  LogCategories,
  LogSubCategories,
  LogTags,
} from "@/services/logger/logger-types";
import { dequeueAppAlertDialog } from "@/store/app-alert-dialog/app-alert-dialog-slice";
import type { DataAccessUpdatePayload } from "@/types";
import { useIsRestoring } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import IDENTIFIERS from "@/constants/test_id";

type AuthGrantProps = {
  grantedBy: string;
  contentType: string;
};

const AuthGrant: React.FC<AuthGrantProps> = ({ grantedBy, contentType }) => {
  const { authData } = useAuth();
  const dispatch = useDispatch();
  const isRestoring = useIsRestoring();
  const demographics = queryClient.getQueryData([
    QueryKeys.DEMOGRAPHICS,
    authData?.user?.username,
  ]) as Demographics;
  const members = queryClient.getQueryData([QueryKeys.MEMBERS]) as Record<
    string,
    Relation
  >;
  const dataAccessDisclaimerContents = queryClient.getQueryData([
    QueryKeys.DATA_ACCESS_CONFIG,
  ]) as DataAccessConfigResponseType;
  const content =
    contentType &&
    !isRestoring &&
    dataAccessDisclaimerContents.authorization[contentType];
  const authGrantPlaceholderReplacement = !isRestoring
    ? `${members[grantedBy].firstName} ${members[grantedBy].lastName}`
    : "";
  const dataAccessUpdateMutation = useDataAccessUpdateMutation();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  // 3.3.4 Error Prevention (Legal, Financial, Data) — AA / 4.1.3 Status Messages — AA:
  // surfaced and announced on failure instead of the dialog silently closing.
  const [updateError, setUpdateError] = useState<string | null>(null);

  const submit = (
    status:
      | typeof DATA_ACCESS_ALL_STATUS.DECLINED
      | typeof DATA_ACCESS_ALL_STATUS.ACCEPTED,
  ) => {
    setUpdateError(null);
    setIsUpdating(true);
    const payload: DataAccessUpdatePayload = {
      memberId: demographics.memberId,
      token: authData!.user!.access_token,
      grantedBy,
      status,
      grantedTo: demographics?.memberId,
    };
    dataAccessUpdateMutation.mutate(payload, {
      onSuccess: () => {
        setIsUpdating(false);
        dispatch(dequeueAppAlertDialog());
      },
      onError: () => {
        setIsUpdating(false);
        setUpdateError(
          "Something went wrong and this choice wasn't saved. Please try again.",
        );
      },
    });
  };

  const onDecline = () => submit(DATA_ACCESS_ALL_STATUS.DECLINED);
  const onAccept = () => submit(DATA_ACCESS_ALL_STATUS.ACCEPTED);

  return (
    !isRestoring && (
      <AlertDialog
        defaultOpen={true}
        logDialogName={LogSubCategories.ACCESS_CONSENT}
        logTag={LogTags.MODULE_ACCESS}
        logCategory={LogCategories.DATA_ACCESS_MANAGEMENT}
        logSubCategory={LogSubCategories.ACCESS_CONSENT}
      >
        <AlertDialogPortal>
          <AlertDialogOverlay className="AlertDialogOverlay" />
          <AlertDialogContent className="AlertDialogContent">
            <AlertDialogTitle className="AlertDialogTitle">
              Disclaimer
            </AlertDialogTitle>
            <AlertDialogDescription className="AlertDialogDescription">
              {content && (
                <span
                  className="text-sm text-muted-foreground"
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHtmlWithPlaceholders(
                      content,
                      DATA_ACCESS_DISCLAIMER_PLACEHOLDER.MEMBER_NAME,
                      authGrantPlaceholderReplacement,
                    ),
                  }}
                />
              )}
            </AlertDialogDescription>
            {/* 4.1.3 Status Messages — AA: mounted unconditionally so the error is
                announced the moment it appears, not silently added to an absent region. */}
            {updateError && (
              <p role="alert" className="text-sm text-destructive">
                {updateError}
              </p>
            )}
            <div
              style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}
            >
              <AlertDialogCancel
                asChild
                disabled={isUpdating}
                onClick={(event) => {
                  // Keep the dialog open until the mutation actually resolves —
                  // AlertDialogCancel closes by default on click.
                  event.preventDefault();
                  onDecline();
                }}
              >
                <Button
                  variant="outline"
                  className="w-32 rounded-sm"
                  disabled={isUpdating}
                  data-testid={
                    IDENTIFIERS.ACCESS_CONSENT_DISCLAIMER.DECLINE_BUTTON
                  }
                  logMetadata={{
                    grantedBy,
                    status: DATA_ACCESS_ALL_STATUS.DECLINED,
                    grantedTo: demographics?.memberId,
                  }}
                >
                  {isUpdating ? (
                    <LoaderCircle className="text-white-500 size-6 animate-spin" />
                  ) : (
                    "Decline"
                  )}
                </Button>
              </AlertDialogCancel>
              <AlertDialogAction
                asChild
                disabled={isUpdating}
                onClick={(event) => {
                  event.preventDefault();
                  onAccept();
                }}
              >
                <Button
                  variant="default"
                  className="w-32 rounded-sm hover:bg-primary-selected"
                  disabled={isUpdating}
                  data-testid={
                    IDENTIFIERS.ACCESS_CONSENT_DISCLAIMER.ACCEPT_BUTTON
                  }
                  logMetadata={{
                    grantedBy,
                    status: DATA_ACCESS_ALL_STATUS.ACCEPTED,
                    grantedTo: demographics?.memberId,
                  }}
                >
                  {isUpdating ? (
                    <LoaderCircle className="text-white-500 size-6 animate-spin" />
                  ) : (
                    "Accept"
                  )}
                </Button>
              </AlertDialogAction>
            </div>
          </AlertDialogContent>
        </AlertDialogPortal>
      </AlertDialog>
    )
  );
};

export default AuthGrant;
